'use client';

import { useTranslations } from 'next-intl';
import { useCallback, useState } from 'react';
import type { ElasticRetailer } from '@//app/api/retailers/[slug]';
import { InlineButton } from '@//components/InlineButton';
import { Link } from '@//components/Link';
import { useLegacyTrackPhoneNumberCallRetailer } from '@//data/tracking/useLegacyTrackPhoneNumberCallRetailer';
import { useLegacyTrackPhoneNumberVisibleRetailer } from '@//data/tracking/useLegacyTrackPhoneNumberVisibleRetailer';
import { formatPhoneNumber, formatPhoneNumberPretty } from '@//utils/phonenumbers';
import styles from '../../RetailerPage.module.scss';

interface RetailerTelephoneProps {
  retailer: ElasticRetailer;
}

const RetailerTelephone = ({ retailer }: RetailerTelephoneProps) => {
  const tCommon = useTranslations('other.common.telephoneNumber');
  const [isVisible, setIsVisible] = useState(false);
  const { trackLegacyTrackPhoneNumberCallRetailer } = useLegacyTrackPhoneNumberCallRetailer();
  const { trackLegacyTrackPhoneNumberVisibleRetailer } = useLegacyTrackPhoneNumberVisibleRetailer();

  const onShowClick = useCallback(() => {
    trackLegacyTrackPhoneNumberVisibleRetailer(retailer);
    setIsVisible(true);
  }, [trackLegacyTrackPhoneNumberVisibleRetailer, retailer]);

  const onPhoneLinkClick = useCallback(
    () => trackLegacyTrackPhoneNumberCallRetailer(retailer),
    [trackLegacyTrackPhoneNumberCallRetailer, retailer],
  );

  const formattedPhoneNumber = retailer.telephoneNumber
    ? formatPhoneNumber(retailer.telephoneNumber)
    : undefined;

  const formattedPhoneNumberPretty = retailer.telephoneNumber
    ? formatPhoneNumberPretty(retailer.telephoneNumber)
    : undefined;

  if (!retailer.telephoneNumber) return null;

  return (
    <>
      <dt className={styles.descriptionListLabel}>
        <abbr title={tCommon('label')}>{tCommon('short')}</abbr>
      </dt>
      <dd className={styles.descriptionListValue}>
        {isVisible ? (
          <Link
            href={`tel:${formattedPhoneNumber}`}
            title={tCommon('linkAriaLabel', {
              name: retailer.name,
              phoneNumber: formattedPhoneNumberPretty ?? '',
            })}
            onClick={onPhoneLinkClick}
            prefetch="none"
          >
            {formattedPhoneNumberPretty}
          </Link>
        ) : (
          <InlineButton onClick={onShowClick} weight="strong">
            {tCommon('showTelephoneNumber')}
          </InlineButton>
        )}
      </dd>
    </>
  );
};

export default RetailerTelephone;
